/* ==========================================================================
 * LUXE SLIDER
 * Infinite responsive carousel
 * ========================================================================== */

class LuxeSlider {
  constructor(element) {
    /*
     * Prevent double initialization from different theme scripts.
     */
    if (element.__luxeSliderInstance) {
      return element.__luxeSliderInstance;
    }

    element.__luxeSliderInstance = this;
    element.__luxeInit = true;

    this.el = element;

    this.track =
      element.querySelector(
        '[data-track]'
      );

    if (!this.track) return;

    this.prevButton =
      element.querySelector(
        '[data-prev]'
      );

    this.nextButton =
      element.querySelector(
        '[data-next]'
      );

    this.originalMarkup =
      this.track.innerHTML;

    this.originalSlides = [];
    this.slides = [];

    this.realCount = 0;
    this.cloneCount = 0;
    this.perView = 1;
    this.slideWidth = 100;

    this.index = 0;

    this.isActive = false;
    this.isAnimating = false;
    this.isDragging = false;

    this.startX = 0;
    this.startY = 0;

    this.gestureAxis = null;

    this.animationTimer = null;
    this.resizeTimer = null;

    this.animationDuration = 450;
    this.layoutSignature = '';

    this.handleResize =
      this.handleResize.bind(this);

    this.handleTransitionEnd =
      this.handleTransitionEnd.bind(
        this
      );

    this.init();
  }

  /* ------------------------------------------------------------------------
   * Responsive state
   * --------------------------------------------------------------------- */

  isMobile() {
    return window.innerWidth < 750;
  }

  isMobileOnlyDisabled() {
    return (
      !this.isMobile() &&
      this.el.classList.contains(
        'luxe-slider--mobile-only'
      )
    );
  }

  getSlidesPerView() {
    let value;

    if (window.innerWidth < 750) {
      value = Number(
        this.el.dataset.mobile
      );
    } else if (
      window.innerWidth < 1025
    ) {
      value = Number(
        this.el.dataset.tablet
      );
    } else {
      value = Number(
        this.el.dataset.desktop
      );
    }

    return Number.isFinite(value) &&
      value > 0
      ? value
      : 1;
  }

  getLayoutSignature() {
    if (
      this.isMobileOnlyDisabled()
    ) {
      return 'disabled';
    }

    const perView =
      this.getSlidesPerView();

    return `active-${perView}`;
  }

  /* ------------------------------------------------------------------------
   * Initialization
   * --------------------------------------------------------------------- */

  init() {
    this.clearTimers();

    this.isAnimating = false;
    this.isDragging = false;

    this.perView =
      this.getSlidesPerView();

    this.layoutSignature =
      this.getLayoutSignature();

    if (
      this.isMobileOnlyDisabled()
    ) {
      this.deactivate();
      this.bindResize();

      return;
    }

    this.track.innerHTML =
      this.originalMarkup;

    this.originalSlides = [
      ...this.track.querySelectorAll(
        '[data-slide]'
      )
    ];

    this.realCount =
      this.originalSlides.length;

    /*
     * Do not activate a carousel when every slide
     * already fits inside the viewport.
     */
    if (
      this.realCount <=
      this.perView
    ) {
      this.deactivate();
      this.bindResize();

      return;
    }

    this.cloneCount = Math.min(
      this.perView,
      this.realCount
    );

    this.createClones();

    this.slides = [
      ...this.track.querySelectorAll(
        '[data-slide]'
      )
    ];

    // Wire up existing per-slide "next" buttons (elements marked with
    // [data-slide-next]) to advance the carousel by one. Do not create
    // new buttons here   the theme already provides them.
    this.track
      .querySelectorAll('[data-slide-next]')
      .forEach((btn) => {
        // Ensure button semantics where possible
        try {
          if (!btn.getAttribute('role') && btn.tagName !== 'BUTTON') {
            btn.setAttribute('role', 'button');
          }
        } catch (e) {}

        if (!btn.getAttribute('aria-label')) {
          btn.setAttribute('aria-label', 'Next slide');
        }

        // Overwrite any existing onclick to avoid duplicate handlers on re-init
        btn.type = btn.type || 'button';
        btn.onclick = (ev) => {
          ev.stopPropagation();
          this.requestMove(1);
        };
      });

    this.preloadSliderImages();

    this.slideWidth =
      100 / this.perView;

    this.index =
      this.cloneCount;

    this.isActive = true;

    this.el.classList.add(
      'is-slider-active'
    );

    this.setButtonsDisabled(false);

    this.update(false);
    this.bindEvents();
    this.bindResize();
  }

  /* ------------------------------------------------------------------------
   * Clones
   * --------------------------------------------------------------------- */

  createClones() {
    const beforeSlides =
      this.originalSlides.slice(
        -this.cloneCount
      );

    const afterSlides =
      this.originalSlides.slice(
        0,
        this.cloneCount
      );

    const beforeFragment =
      document.createDocumentFragment();

    const afterFragment =
      document.createDocumentFragment();

    /*
     * A fragment preserves the correct slide order.
     * Repeated prepend() calls would reverse the clones.
     */
    beforeSlides.forEach(
      (slide) => {
        const clone =
          this.createClone(slide);

        beforeFragment.appendChild(
          clone
        );
      }
    );

    afterSlides.forEach(
      (slide) => {
        const clone =
          this.createClone(slide);

        afterFragment.appendChild(
          clone
        );
      }
    );

    this.track.insertBefore(
      beforeFragment,
      this.track.firstChild
    );

    this.track.appendChild(
      afterFragment
    );
  }

  createClone(slide) {
    const clone =
      slide.cloneNode(true);

    clone.setAttribute(
      'data-clone',
      ''
    );

    clone.setAttribute(
      'aria-hidden',
      'true'
    );

    clone
      .querySelectorAll(
        '[id]'
      )
      .forEach((element) => {
        element.removeAttribute(
          'id'
        );
      });

    return clone;
  }

  /* ------------------------------------------------------------------------
   * Image loading
   * --------------------------------------------------------------------- */

  preloadSliderImages() {
    this.track
      .querySelectorAll('img')
      .forEach((image) => {
        /*
         * Product cards and their clones must be ready
         * before they enter the visible viewport.
         */
        image.loading = 'eager';

        image.setAttribute(
          'loading',
          'eager'
        );

        /*
         * Support custom lazy-loading attributes too.
         */
        if (
          image.dataset.src &&
          !image.getAttribute('src')
        ) {
          image.src =
            image.dataset.src;
        }

        if (
          image.dataset.srcset &&
          !image.getAttribute('srcset')
        ) {
          image.srcset =
            image.dataset.srcset;
        }

        image
          .decode?.()
          .catch(() => {});
      });
  }

  /* ------------------------------------------------------------------------
   * Public navigation
   * --------------------------------------------------------------------- */

  next() {
    this.requestMove(1);
  }

  prev() {
    this.requestMove(-1);
  }

  requestMove(direction) {
    if (
      !this.isActive ||
      this.isAnimating
    ) {
      return;
    }

    this.move(direction);
  }

  move(direction) {
    if (!this.isActive) return;

    this.isAnimating = true;
    this.index += direction;

    this.update(true);

    /*
     * transitionend is the primary completion event.
     * The timer is only a safety fallback.
     */
    clearTimeout(
      this.animationTimer
    );

    this.animationTimer =
      setTimeout(() => {
        this.finishTransition();
      }, this.animationDuration + 80);
  }

  /* ------------------------------------------------------------------------
   * Track position
   * --------------------------------------------------------------------- */

  update(animate = true) {
    if (!this.isActive) return;

    this.track.style.transition =
      animate
        ? `transform ${this.animationDuration}ms ease`
        : 'none';

    const offset =
      this.index *
      this.slideWidth;

    this.track.style.transform =
      `translate3d(-${offset}%, 0, 0)`;
  }

  jumpTo(index) {
    this.index = index;

    this.track.style.transition =
      'none';

    const offset =
      this.index *
      this.slideWidth;

    this.track.style.transform =
      `translate3d(-${offset}%, 0, 0)`;

    /*
     * Flush the instant jump before another
     * animated movement begins.
     */
    void this.track.offsetWidth;
  }

  /* ------------------------------------------------------------------------
   * Infinite loop
   * --------------------------------------------------------------------- */

  checkLoop() {
    if (!this.isActive) return;

    if (
      this.index >=
      this.realCount +
        this.cloneCount
    ) {
      this.jumpTo(
        this.cloneCount
      );

      return;
    }

    if (
      this.index <
      this.cloneCount
    ) {
      this.jumpTo(
        this.realCount +
          this.index
      );
    }
  }

  handleTransitionEnd(event) {
    if (
      event.target !==
      this.track
    ) {
      return;
    }

    if (
      event.propertyName !==
      'transform'
    ) {
      return;
    }

    this.finishTransition();
  }

  finishTransition() {
    if (!this.isAnimating) return;

    clearTimeout(
      this.animationTimer
    );

    this.checkLoop();

    this.isAnimating = false;
  }


  /* ------------------------------------------------------------------------
   * Touch gestures
   * --------------------------------------------------------------------- */

  touchStart(event) {
    if (
      !this.isActive ||
      this.isAnimating ||
      event.touches.length !== 1
    ) {
      return;
    }

    this.startX =
      event.touches[0].clientX;

    this.startY =
      event.touches[0].clientY;

    this.isDragging = true;
    this.gestureAxis = null;

    this.track.style.transition =
      'none';
  }

  touchMove(event) {
    if (
      !this.isDragging ||
      !this.isActive ||
      event.touches.length !== 1
    ) {
      return;
    }

    const currentX =
      event.touches[0].clientX;

    const currentY =
      event.touches[0].clientY;

    const differenceX =
      currentX - this.startX;

    const differenceY =
      currentY - this.startY;

    if (!this.gestureAxis) {
      if (
        Math.abs(differenceX) < 6 &&
        Math.abs(differenceY) < 6
      ) {
        return;
      }

      this.gestureAxis =
        Math.abs(differenceX) >
        Math.abs(differenceY)
          ? 'x'
          : 'y';
    }

    /*
     * Leave vertical page scrolling untouched.
     */
    if (
      this.gestureAxis === 'y'
    ) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    const offset =
      this.index *
      this.slideWidth;

    this.track.style.transform =
      `translate3d(
        calc(-${offset}% + ${differenceX}px),
        0,
        0
      )`;
  }

  touchEnd(event) {
    if (
      !this.isDragging ||
      !this.isActive
    ) {
      return;
    }

    this.isDragging = false;

    if (
      this.gestureAxis !== 'x'
    ) {
      this.update(true);
      return;
    }

    const touch =
      event.changedTouches[0];

    if (!touch) {
      this.update(true);
      return;
    }

    const movedX =
      touch.clientX -
      this.startX;

    if (movedX < -50) {
      this.requestMove(1);
    } else if (movedX > 50) {
      this.requestMove(-1);
    } else {
      this.update(true);
    }
  }

  touchCancel() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.gestureAxis = null;

    this.update(true);
  }

  /* ------------------------------------------------------------------------
   * Responsive rebuild
   * --------------------------------------------------------------------- */

  handleResize() {
    clearTimeout(
      this.resizeTimer
    );

    this.resizeTimer =
      setTimeout(() => {
        const nextSignature =
          this.getLayoutSignature();

        /*
         * Mobile browser toolbars may fire resize
         * without changing the carousel layout.
         */
        if (
          nextSignature ===
          this.layoutSignature
        ) {
          return;
        }

        this.init();
      }, 150);
  }

  bindResize() {
    window.removeEventListener(
      'resize',
      this.handleResize
    );

    window.addEventListener(
      'resize',
      this.handleResize
    );
  }

  /* ------------------------------------------------------------------------
   * Events
   * --------------------------------------------------------------------- */

  bindEvents() {
    if (this.nextButton) {
      this.nextButton.onclick =
        () => this.next();
    }

    if (this.prevButton) {
      this.prevButton.onclick =
        () => this.prev();
    }

    this.track.removeEventListener(
      'transitionend',
      this.handleTransitionEnd
    );

    this.track.addEventListener(
      'transitionend',
      this.handleTransitionEnd
    );

    this.track.ontouchstart =
      (event) => {
        this.touchStart(event);
      };

    this.track.ontouchmove =
      (event) => {
        this.touchMove(event);
      };

    this.track.ontouchend =
      (event) => {
        this.touchEnd(event);
      };

    this.track.ontouchcancel =
      () => {
        this.touchCancel();
      };
  }

  /* ------------------------------------------------------------------------
   * Inactive state
   * --------------------------------------------------------------------- */

  deactivate() {
    this.isActive = false;
    this.isAnimating = false;
    this.isDragging = false;

    this.track.innerHTML =
      this.originalMarkup;

    this.track.style.transform =
      '';

    this.track.style.transition =
      '';

    this.track.style.display = '';

    this.el.classList.remove(
      'is-slider-active'
    );

    this.setButtonsDisabled(true);

    if (this.nextButton) {
      this.nextButton.onclick = null;
    }

    if (this.prevButton) {
      this.prevButton.onclick = null;
    }

    this.track.ontouchstart = null;
    this.track.ontouchmove = null;
    this.track.ontouchend = null;
    this.track.ontouchcancel = null;
  }

  setButtonsDisabled(disabled) {
    if (this.prevButton) {
      this.prevButton.disabled =
        disabled;
    }

    if (this.nextButton) {
      this.nextButton.disabled =
        disabled;
    }
  }

  clearTimers() {
    clearTimeout(
      this.animationTimer
    );

    clearTimeout(
      this.resizeTimer
    );
  }
}

/* ==========================================================================
 * INITIALIZATION
 * ========================================================================== */

function initLuxeSliderElements(
  root = document
) {
  root
    .querySelectorAll(
      '[data-luxe-slider]'
    )
    .forEach((slider) => {
      if (
        slider.__luxeSliderInstance
      ) {
        return;
      }

      new LuxeSlider(slider);
    });
}

if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initLuxeSliderElements();
    },
    {
      once: true
    }
  );
} else {
  initLuxeSliderElements();
}

window.addEventListener(
  'shopify:section:load',
  (event) => {
    initLuxeSliderElements(
      event.target
    );
  }
);